import { useEffect, useState } from 'react';
import { FormField, TextInput, Select } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { adminApi } from '@/shared/api/adminApi';

/** שיוך פעולה בודדת שרץ כשקוד-הסיום/התגית נבחרים. */
export type ActionBinding =
  | { type: 'automation'; id: number }
  | { type: 'webhook'; id: number }
  | { type: 'api'; url: string; secret?: string };

interface Opt { id: number; name: string }

/**
 * עורך "הפעל אוטומציה / Webhook / API" לקוד-סיום או תגית. כשהנציג בוחר את
 * הקוד/תגית — הפעולות המשויכות רצות. הרשימות נטענות מ-hub (אותן אוטומציות/
 * webhooks שנוצרו בהגדרות).
 */
export function ActionBindingsEditor({ value, onChange }: { value: ActionBinding[]; onChange: (next: ActionBinding[]) => void }) {
  const [automations, setAutomations] = useState<Opt[]>([]);
  const [webhooks, setWebhooks] = useState<Opt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // עטיפה דפנסיבית — אם ה-API לא זמין (או ממוק חלקית) לא מפילים את ה-drawer.
    const loadAutomations = async () => {
      try { return (await adminApi.hubAutomations?.()) ?? []; } catch { return []; }
    };
    const loadWebhooks = async () => {
      try { return (await adminApi.hubWebhooks?.())?.data ?? []; } catch { return []; }
    };
    void Promise.all([loadAutomations(), loadWebhooks()]).then(([auto, hooks]) => {
      if (!alive) return;
      setAutomations(auto.map((a) => ({ id: a.id, name: a.name })));
      setWebhooks(hooks.map((h) => ({ id: h.id, name: h.name })));
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  function add(type: ActionBinding['type']) {
    if (type === 'api') onChange([...value, { type: 'api', url: '' }]);
    else onChange([...value, { type, id: 0 }]);
  }
  function update(i: number, patch: Partial<ActionBinding>) {
    onChange(value.map((a, j) => (j === i ? ({ ...a, ...patch } as ActionBinding) : a)));
  }
  function remove(i: number) { onChange(value.filter((_, j) => j !== i)); }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
        פעולות שירוצו אוטומטית ברגע שהנציג בוחר בקוד/תגית הזה — אוטומציה, Webhook, או קריאת API לכתובת.
      </div>

      {value.length === 0 && <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>אין פעולות משויכות.</div>}

      {value.map((a, i) => (
        <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 8, background: 'var(--surface-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: a.type === 'api' ? 8 : 0 }}>
            <Icon name={a.type === 'automation' ? 'bolt' : a.type === 'webhook' ? 'link' : 'globe'} size={14} color="var(--accent)" />
            <strong style={{ fontSize: 'var(--t-xs)', flex: 1 }}>
              {a.type === 'automation' ? 'אוטומציה' : a.type === 'webhook' ? 'Webhook' : 'API (כתובת)'}
            </strong>
            <button type="button" className="btn ghost" onClick={() => remove(i)} aria-label="הסר" title="הסר" style={{ height: 24, padding: '0 6px' }}>
              <Icon name="close" size={13} />
            </button>
          </div>

          {a.type === 'automation' && (
            <Select
              value={a.id ? String(a.id) : ''}
              onChange={(v) => update(i, { id: Number(v) } as Partial<ActionBinding>)}
              options={[{ value: '', label: loading ? 'טוען…' : 'בחר אוטומציה…' }, ...automations.map((o) => ({ value: String(o.id), label: o.name }))]}
            />
          )}
          {a.type === 'webhook' && (
            <Select
              value={a.id ? String(a.id) : ''}
              onChange={(v) => update(i, { id: Number(v) } as Partial<ActionBinding>)}
              options={[{ value: '', label: loading ? 'טוען…' : 'בחר Webhook…' }, ...webhooks.map((o) => ({ value: String(o.id), label: o.name }))]}
            />
          )}
          {a.type === 'api' && (
            <div className="stack" style={{ gap: 6 }}>
              <TextInput value={a.url} onChange={(e) => update(i, { url: e.target.value } as Partial<ActionBinding>)} dir="ltr" placeholder="https://..." />
              <TextInput value={a.secret ?? ''} onChange={(e) => update(i, { secret: e.target.value } as Partial<ActionBinding>)} dir="ltr" placeholder="סוד חתימה (אופציונלי)" />
            </div>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="btn ghost" onClick={() => add('automation')} style={{ fontSize: 'var(--t-xs)' }}><Icon name="bolt" size={12} /> אוטומציה</button>
        <button type="button" className="btn ghost" onClick={() => add('webhook')} style={{ fontSize: 'var(--t-xs)' }}><Icon name="link" size={12} /> Webhook</button>
        <button type="button" className="btn ghost" onClick={() => add('api')} style={{ fontSize: 'var(--t-xs)' }}><Icon name="globe" size={12} /> API</button>
      </div>
    </div>
  );
}

/** מסנן שיוכים ריקים (בלי id/url) לפני שמירה. */
export function cleanActionBindings(actions: ActionBinding[]): ActionBinding[] {
  return actions.filter((a) => (a.type === 'api' ? a.url.trim() !== '' : a.id > 0));
}
